/**
 * Local ledger of trade-offer response operations (2026-09-25).
 *
 * Why it exists: a submitted response (decline, in the reported incident)
 * silently vanished when the user left the page before the critical write
 * settled. The ledger converts silent loss into a visible, recoverable
 * event: each submit records its intent locally, and when the offer page is
 * next opened, the record is compared against the authoritative offer state
 * to produce one of three verdicts: confirmed, lost, or ambiguous.
 *
 * The verdict deliberately distinguishes a genuine loss from an offer that
 * merely changed later: "the offer looks different" cannot tell "my decline
 * vanished" apart from "the other party countered afterwards", and falsely
 * claiming a system loss would be worse than saying nothing. Lost is only
 * asserted when the offer is provably unchanged since the attempt
 * (updatedAt still equals the preflight-observed version).
 *
 * This is a per-offer STATUS record, not an outbox: nothing here retries
 * writes on its own. Auto-resubmitting a stale response against an offer
 * that may have changed is exactly the class of unattended state-machine
 * action this codebase avoids.
 *
 * Bounds (agreed before build): one record per offer (a new submit
 * supersedes the old), 20 offers max, 14-day expiry, fixed field sizes,
 * primitives only. No listing names, messages, addresses, payment data, or
 * raw error objects. Fully functional if localStorage is unavailable.
 */

const STORAGE_KEY = 'pxcOfferOps_v1';
const MAX_OFFERS = 20;
const EXPIRY_MS = 14 * 24 * 60 * 60 * 1000;
const MAX_FIELD = 60;

function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object') return {};
    // Expiry prune on every load.
    const now = Date.now();
    for (const key of Object.keys(parsed)) {
      if (!parsed[key] || typeof parsed[key].createdAt !== 'number' || now - parsed[key].createdAt > EXPIRY_MS) {
        delete parsed[key];
      }
    }
    return parsed;
  } catch (e) {
    return {};
  }
}

function saveAll(all) {
  try {
    const keys = Object.keys(all);
    if (keys.length > MAX_OFFERS) {
      // Drop oldest records beyond the cap.
      keys
        .sort((a, b) => (all[a].createdAt || 0) - (all[b].createdAt || 0))
        .slice(0, keys.length - MAX_OFFERS)
        .forEach((k) => delete all[k]);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    /* storage unavailable/full: ledger degrades to no-op, app unaffected */
  }
}

const clip = (v) => String(v).slice(0, MAX_FIELD);

/** Called at submit start. Supersedes any prior record for this offer. */
export function beginOperation({ offerId, responseType, preflightUpdatedAt }) {
  const opId = Math.random().toString(16).slice(2, 10);
  const all = loadAll();
  const prior = all[offerId];
  if (prior && prior.status !== 'confirmed') {
    // A resubmit makes the old record moot; keep the verdict honest.
    prior.status = 'superseded';
  }
  all[clip(offerId)] = {
    v: 1,
    opId,
    offerId: clip(offerId),
    responseType: clip(responseType),
    createdAt: Date.now(),
    writeIssuedAt: null,
    confirmedAt: null,
    status: 'issuing',
    preflightUpdatedAt: typeof preflightUpdatedAt === 'number' ? preflightUpdatedAt : 0,
    failCode: null,
  };
  saveAll(all);
  return opId;
}

function update(offerId, opId, patch) {
  const all = loadAll();
  const rec = all[offerId];
  if (!rec || rec.opId !== opId) return; // superseded or pruned: never resurrect
  Object.assign(rec, patch);
  saveAll(all);
}

export function markQueued(offerId, opId, preflightUpdatedAt) {
  update(offerId, opId, {
    status: 'queued',
    writeIssuedAt: Date.now(),
    ...(typeof preflightUpdatedAt === 'number' ? { preflightUpdatedAt } : {}),
  });
}

export function markConfirmed(offerId, opId) {
  update(offerId, opId, { status: 'confirmed', confirmedAt: Date.now() });
}

export function markFailed(offerId, opId, failCode) {
  update(offerId, opId, { status: 'failed', failCode: clip(failCode || 'unknown') });
}

/** A pending (non-confirmed, non-superseded) record for this offer, if any. */
export function getPendingRecord(offerId) {
  const rec = loadAll()[offerId];
  if (!rec) return null;
  if (rec.status === 'confirmed' || rec.status === 'superseded') return null;
  return rec;
}

export function clearRecord(offerId) {
  const all = loadAll();
  if (all[offerId]) {
    delete all[offerId];
    saveAll(all);
  }
}

const STATUS_FOR_RESPONSE = { accept: 'accepted', decline: 'declined', counter: 'countered' };

function toSeconds(updatedAt) {
  if (!updatedAt) return 0;
  if (typeof updatedAt === 'number') return updatedAt > 1e12 ? Math.round(updatedAt / 1000) : updatedAt;
  if (typeof updatedAt.seconds === 'number') return updatedAt.seconds;
  return 0;
}

/**
 * Pure verdict: compare a pending record against the authoritative offer.
 * Returns 'confirmed' | 'lost' | 'ambiguous'.
 *
 * - confirmed: the offer carries the intended response from this user
 *   (matching history entry no older than the attempt, or the intended
 *   status transition with this user as the last actor). A late-landing
 *   write counts as success.
 * - lost: the offer's updatedAt still equals the version observed at
 *   preflight, so nothing at all was recorded since the attempt.
 * - ambiguous: the offer changed, but not by this response. The UI must say
 *   "may not have been recorded because this offer changed", never assert a
 *   system loss.
 */
export function resolveAgainstOffer(record, offerData, uid) {
  if (!record || !offerData) return 'ambiguous';

  // 60s of slack: the history timestamp comes from the same device's clock,
  // but the record and the entry are written at slightly different moments.
  const sinceMs = record.createdAt - 60_000;
  const history = Array.isArray(offerData.history) ? offerData.history : [];
  const mine = history.some((entry) => {
    if (!entry || entry.type !== 'response' || entry.from !== uid) return false;
    if (record.responseType && entry.responseType !== record.responseType) return false;
    const ts = entry.timestamp;
    const entryMs = typeof ts?.toMillis === 'function' ? ts.toMillis()
      : ts instanceof Date ? ts.getTime()
        : typeof ts?.seconds === 'number' ? ts.seconds * 1000
          : typeof ts === 'number' ? ts : 0;
    return entryMs >= sinceMs;
  });
  if (mine) return 'confirmed';

  const intendedStatus = STATUS_FOR_RESPONSE[record.responseType];
  if (intendedStatus && offerData.status === intendedStatus) {
    // Status alone is not proof for counters (the other party's counter also
    // yields 'countered'), so require this user to be the last actor.
    const actions = history.filter((e) => e && (e.type === 'offer' || e.type === 'response'));
    const lastFrom = actions.length ? actions[actions.length - 1].from : null;
    if (lastFrom === uid) return 'confirmed';
  }

  const currentVersion = toSeconds(offerData.updatedAt);
  const observedVersion = toSeconds(record.preflightUpdatedAt);
  if (currentVersion !== 0 && currentVersion === observedVersion) return 'lost';

  return 'ambiguous';
}
